import React, { FC, useState } from 'react';
import { HiOutlineCalendar, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineClock } from 'react-icons/hi';
import { Button } from '../../../../../ui/Button';
import NewScheduleForm, { Schedule } from './NewScheduleForm';
import { Card } from '../../../../../ui/Card';

interface ScheduledGroupToursProps {
  values: any;
  setFieldValue: any;
}

const ScheduledGroupTours: FC<ScheduledGroupToursProps> = ({ values, setFieldValue }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const schedules: Schedule[] = values.schedules || [];

  const handleSave = (schedule: Schedule) => {
    if (editingId) {
       const updatedSchedules = schedules.map((s, index) => 
         (s.id === editingId && s.id !== undefined) || index.toString() === editingId ? schedule : s
       );
       setFieldValue('schedules', updatedSchedules);
       setEditingId(null);
    } else {
       setFieldValue('schedules', [...schedules, schedule]);
       setIsAdding(false);
    }
  };

  const handleDelete = (id: string) => {
    setFieldValue('schedules', schedules.filter((s, index) => s.id !== id && index.toString() !== id));
  };

  const handleEdit = (id: string) => {
      setEditingId(id);
      setIsAdding(false);
  };

  const formatDate = (dateString: string) => {
      if(!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className='space-y-6'>
       <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlineClock className='w-6 h-6' />
          <div>
            <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Scheduled Group Tours</h3>
            <p className='text-sm text-gray-500 dark:text-gray-400'>Manage departure dates, slots, and pricing for group tours.</p>
          </div>
       </div>

       {/* List of Schedules */}
       {!isAdding && !editingId && (
         <div className='space-y-4'>
            {schedules.length === 0 ? (
                <div className='text-center py-10 bg-gray-50 dark:bg-gray-800/30 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700'>
                    <HiOutlineCalendar className='w-10 h-10 text-gray-300 mx-auto mb-3' />
                    <h4 className='text-gray-900 dark:text-gray-100 font-medium'>No schedules added yet</h4>
                    <p className='text-gray-500 text-sm mb-4'>Start by adding a new departure date.</p>
                    <Button onClick={() => setIsAdding(true)} leftIcon={<HiOutlinePlus className='w-4 h-4'/>}>
                        Add First Schedule
                    </Button>
                </div>
            ) : (
                <div className='grid grid-cols-1 gap-5'>
                    {schedules.map((schedule, index) => (
                        <Card 
                          key={schedule.id || schedule.startDate} 
                          className='p-0 overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-300'
                        >
                            <div className='flex flex-col md:flex-row'>
                                {/* Date Badge Column */}
                                <div className='md:w-48 bg-gray-50 dark:bg-gray-800/80 p-5 flex flex-col justify-center border-b md:border-b-0 md:border-r border-gray-100 dark:border-gray-700'>
                                    <div className='flex items-center gap-2 text-primary-600 dark:text-primary-400 mb-2'>
                                        <HiOutlineCalendar className='w-4 h-4' />
                                        <span className='text-[10px] font-bold uppercase tracking-widest'>Departure</span>
                                    </div>
                                    <div className='font-bold text-gray-900 dark:text-white leading-tight'>
                                        {formatDate(schedule.startDate)}
                                    </div>
                                    <div className='text-xs text-gray-500 mt-1'>to {formatDate(schedule.endDate)}</div>
                                </div>

                                {/* Content Body */}
                                <div className='flex-1 p-5'>
                                    <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
                                        <div className='space-y-3'>
                                            <div className='flex items-center gap-3'>
                                                <h4 className='font-bold text-gray-900 dark:text-white capitalize'>
                                                    {schedule.startLocation} ➝ {schedule.endLocation}
                                                </h4>
                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                    schedule.bookingType === 'instant' ?
                                                    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                }`}>
                                                    {schedule.bookingType === 'instant' ? '⚡ Instant' : '📞 Contact'}
                                                </span>
                                            </div>
                                            
                                            <div className='flex flex-wrap gap-x-6 gap-y-2'>
                                                <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                                                    <span className='w-1.5 h-1.5 rounded-full bg-primary-500' />
                                                    <span className='font-medium'>{schedule.totalSlots} Slots Overall</span>
                                                </div>
                                                <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                                                    <span className='w-1.5 h-1.5 rounded-full bg-indigo-500' />
                                                    <span className='font-medium'>{schedule.accommodations?.length || 0} Room Types</span>
                                                </div>
                                            </div>

                                            {/* Room types breakdown */}
                                            {schedule.accommodations && schedule.accommodations.length > 0 && (
                                                <div className='flex flex-wrap gap-2 mt-2'>
                                                    {schedule.accommodations.map((acc) => (
                                                        <div key={acc.id || acc.name} className='px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-[10px] font-medium flex items-center gap-2 border border-gray-200 dark:border-gray-700'>
                                                            <span className='text-gray-700 dark:text-gray-300'>{acc.name}</span>
                                                            <span className={acc.spacesLeft > 0 ? 'text-blue-600 font-bold' : 'text-rose-600 font-bold'}>
                                                                {acc.spacesLeft > 0 ? `${acc.spacesLeft} left` : 'Sold Out'}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className='flex items-center gap-2 pt-4 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-gray-700'>
                                            <button 
                                                type='button'
                                                onClick={() => handleEdit(schedule.id || index.toString())}
                                                className='p-2.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all'
                                                title='Edit schedule'
                                            >
                                                <HiOutlinePencil className='w-5 h-5'/>
                                            </button>
                                             <button 
                                                type='button'
                                                onClick={() => handleDelete(schedule.id || index.toString())}
                                                className='p-2.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all'
                                                title='Delete schedule'
                                            >
                                                <HiOutlineTrash className='w-5 h-5'/>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                    <div className='flex justify-center pt-4'>
                         <Button 
                            onClick={() => setIsAdding(true)} 
                            variant='outline-secondary' 
                            leftIcon={<HiOutlinePlus className='w-4 h-4'/>}
                            className='!px-8'
                          >
                            Add Another Schedule
                        </Button>
                    </div>
                </div>
            )}
         </div>
       )}

       {/* Form Area */}
       {(isAdding || editingId) && (
           <NewScheduleForm 
             initialData={editingId ? schedules.find(s => s.id === editingId) : undefined} 
             onSave={handleSave}
             onCancel={() => {
                 setIsAdding(false);
                 setEditingId(null);
             }}
           />
       )}
    </div>
  );
};

export default ScheduledGroupTours;
